import { useEffect, useRef } from "react";
import { useReactFlow, useStore } from "@xyflow/react";
import type * as Y from "yjs";
import { useReducedMotion } from "../hooks/useReducedMotion.js";
import type { CanvasRevealRequest } from "./types.js";

/**
 * Bring ONE widget into view, on request, and say which one to highlight.
 *
 * Rendered inside `<ReactFlowProvider>` so it can use the same `fitBounds`
 * navigation the phase strip already uses — this is not a second way to move
 * the canvas, it is the existing one pointed at a widget's rect instead of a
 * phase's.
 *
 * It reads the widget's CURRENT rect from the Y.Doc at the moment of the
 * request. That is what makes a moved or resized widget still reachable: the
 * caller supplies an identity, never coordinates, and a stale saved position
 * from whenever a proposal was written is never consulted.
 *
 * Nothing here selects, opens or writes anything. A reveal is a viewport
 * change, so it cannot disturb content, an open editor, or anyone's focus.
 */

/** Padding around the widget, as a fraction of the fitted rect. */
const REVEAL_PADDING = 0.6;
/** Keep some visible canvas around an oversized newly added widget. */
const PRESERVE_ZOOM_PADDING = 0.2;
/** How long the pan takes. Zero under `prefers-reduced-motion`. */
const REVEAL_DURATION_MS = 420;
/** How long the destination stays outlined afterwards. */
export const CANVAS_REVEAL_HIGHLIGHT_MS = 1_800;

export function CanvasRevealController({
  request,
  widgetsMap,
  onHighlight,
}: {
  readonly request: CanvasRevealRequest | undefined;
  readonly widgetsMap: Y.Map<Y.Map<unknown>>;
  readonly onHighlight: (widgetId: string | null) => void;
}): null {
  const flow = useReactFlow();
  const paneW = useStore((s) => s.width);
  const paneH = useStore((s) => s.height);
  const reducedMotion = useReducedMotion();
  const servedNonce = useRef<number | null>(null);
  /**
   * The latest `onHighlight`, held so the outline's lifetime does not depend on
   * the callback's identity.
   *
   * A host that passes an inline lambda would otherwise restart the countdown
   * on every render, and the expiry would fire through whichever closure
   * happened to be current — so a parent that has been replaced could be
   * written to. Written in its own effect rather than during render, which
   * React forbids; `useRef` seeds it for the first pass, and effects run in
   * declaration order, so this one lands before both effects below.
   */
  const highlightRef = useRef(onHighlight);
  useEffect(() => {
    highlightRef.current = onHighlight;
  }, [onHighlight]);

  /**
   * The nonce whose pan actually happened — so the outline can tell "revealed"
   * from "asked for, but the widget was gone".
   */
  const outlinedNonce = useRef<number | null>(null);
  /*
   * The two PRIMITIVES the outline's lifetime is keyed on.
   *
   * This split is the whole point of the component's shape. The pan is
   * idempotent per nonce; the outline is a DURATION. Expressed in one effect,
   * the second was hostage to the first's guard: any re-run — a freshly
   * allocated same-nonce request, a new `useReactFlow` identity, a
   * `prefers-reduced-motion` change, StrictMode's mount replay — ran the
   * cleanup, killing the pending timer, and then returned early at the nonce
   * guard, so nothing was ever rescheduled and the outline stayed on for ever.
   *
   * Keyed on the nonce and the id rather than on the request OBJECT, every one
   * of those re-runs leaves the countdown alone, because none of them changes
   * either number. A genuinely new reveal changes the nonce, and that restarts
   * a whole duration rather than inheriting the remainder of the last one.
   */
  const nonce = request?.nonce;
  const revealedWidgetId = request?.widgetId;
  const measurable = !request?.preserveZoom || (paneW > 0 && paneH > 0);

  // 1. Serve a request: move the viewport, once per nonce.
  useEffect(() => {
    if (request === undefined) return;
    // The nonce, not the id: revealing the same widget twice in a row is a
    // second request and must move the canvas again.
    if (servedNonce.current === request.nonce) return;
    if (!measurable) return;
    servedNonce.current = request.nonce;

    const entry = widgetsMap.get(request.widgetId);
    // Gone between the request and this effect. The caller has already told the
    // reader what happened; moving the canvas to nowhere would only confuse.
    if (!entry) return;

    const x = numberOf(entry, "x", 0);
    const y = numberOf(entry, "y", 0);
    const w = numberOf(entry, "w", 220);
    const h = numberOf(entry, "h", 150);
    const duration = reducedMotion ? 0 : REVEAL_DURATION_MS;
    if (request.preserveZoom) {
      flow.setCenter(x + w / 2, y + h / 2, {
        zoom: Math.min(
          flow.getZoom(),
          paneW / (w * (1 + 2 * PRESERVE_ZOOM_PADDING)),
          paneH / (h * (1 + 2 * PRESERVE_ZOOM_PADDING)),
        ),
        duration,
      });
    } else {
      flow.fitBounds(
        { x, y, width: w, height: h },
        { padding: REVEAL_PADDING, duration },
      );
    }
    outlinedNonce.current = request.nonce;
  }, [flow, measurable, paneH, paneW, reducedMotion, request, widgetsMap]);

  // 2. Own how long the outline lasts, and nothing else.
  useEffect(() => {
    // Effects run in declaration order, so the pan above has already recorded
    // whether this nonce reached a real widget by the time this runs.
    if (
      nonce === undefined ||
      revealedWidgetId === undefined ||
      !measurable ||
      outlinedNonce.current !== nonce
    ) {
      return;
    }
    highlightRef.current(revealedWidgetId);
    const clear = setTimeout(
      () => highlightRef.current(null),
      CANVAS_REVEAL_HIGHLIGHT_MS,
    );
    return () => {
      clearTimeout(clear);
      // Cleared here as well as on expiry, so the outline cannot outlive the
      // run that owns it — a following reveal that found nothing, or a host
      // that withdraws the request, would otherwise leave it painted.
      highlightRef.current(null);
    };
  }, [measurable, nonce, revealedWidgetId]);

  return null;
}

function numberOf(
  entry: Y.Map<unknown>,
  key: string,
  fallback: number,
): number {
  const value = entry.get(key);
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
